import type { FC } from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '@interstate/components/Modal';
import { Typography } from '@interstate/components/Typography';
import * as incompleteTradeModalActionCreators from '@makemydeal/dr-dash-store/actions/incompleteTradeModalActionCreators';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import * as tradeYmmtSelectors from '@makemydeal/dr-dash-store/selectors/tradeYmmtSelectors';

const TITLE = 'Incomplete trade information';
const PROMPT =
    "Complete the required trade details (year, make, model, trim, and allowance) before saving.\nIf you continue without completing this information, the trade will not be included in the customer's offer.";

const PREFIX = 'incomplete-trade-modal';

export interface IncompleteTradeModalProps {
    show: boolean;
    onContinue?: () => void;
}

/**
 * Modal that warns the user about incomplete trade YMMT information
 * and gives them the option to continue anyway or go back to fix it.
 * Handles its own click logic internally using Redux.
 */
export const IncompleteTradeModal: FC<IncompleteTradeModalProps> = ({ show, onContinue }) => {
    const dispatch = useDispatch();
    const incompleteTradeIndices = useSelector(tradeYmmtSelectors.getIncompleteTradeIndices);

    const handleContinue = useCallback(() => {
        // Per Rally US1865170 AC#3: Set shouldIncludeTradeIn=false for incomplete trades
        // and trigger payment call
        // Multiple trade-ins: Update each incomplete trade
        incompleteTradeIndices.forEach((index) => {
            dispatch(tradeInsActionCreators.includeTradeChanged(false, index, true));
        });

        // Set bypass flag — middleware will trigger save once PAYMENT_RECEIVED fires
        dispatch(incompleteTradeModalActionCreators.setBypassIncompleteTradeCheck());

        // Notify parent to show UpdateOffer dialog (if callback provided)
        onContinue?.();
    }, [dispatch, incompleteTradeIndices, onContinue]);

    const handleCancel = useCallback(() => {
        dispatch(incompleteTradeModalActionCreators.hideIncompleteTradeModal());
    }, [dispatch]);
    const footer = {
        layout: 'rightAligned' as const,
        options: {
            action: handleCancel,
            label: 'Cancel',
            buttonStyle: 'tertiary' as const,
            customIdentifiers: {
                optionButton0: {
                    dataTestId: `${PREFIX}-cancel-btn`
                }
            }
        },
        primary: {
            action: handleContinue,
            label: 'Continue',
            customIdentifiers: {
                primaryButton: {
                    dataTestId: `${PREFIX}-continue-btn`
                }
            }
        }
    };

    return (
        <Modal
            show={show}
            size="large"
            onHide={handleCancel}
            header={
                <Typography variant="h4" tag="h4">
                    {TITLE}
                </Typography>
            }
            footer={footer}
            data-testid={PREFIX}
        >
            <Typography variant="body-sm" tag="p">
                {PROMPT}
            </Typography>
        </Modal>
    );
};
